/**
 * The read path of the offline layer: strategy dispatch under `apiGet`.
 *
 * `cache-first` serves a cached copy and revalidates in the background when
 * it is older than the policy's ttl; `network-first` asks the server and
 * falls back to the cache only on a network failure. Every successful read
 * under either strategy is written through, which is what makes the cache
 * "what you have opened".
 *
 * Account and generation are resolved once, when the request starts, and
 * carried through every step of it: a sign-out (or a different sign-in) while
 * a response is in flight can then never steer its write into the wrong
 * store, and a wipe voids it outright.
 */

import { logger } from '@/utils/logger';

import { currentAccountId, currentStoreGeneration } from './account';
import { applyEchoes, queuedRowsIn, revisionKey } from './echo';
import { OfflineError, isNetworkError } from './errors';
import { noteFirstCacheWrite, recordFreshness } from './freshness';
import { readPolicyFor } from './policies';
import { deleteResource, getResource, listOutbox, withStores } from './store';
import { withoutFragment } from './url';
import { OFFLINE_CHANNEL, TAB_ID } from './wipe';

import type { ReadPolicy } from './policies';
import type { OfflineTx, ResourceRow } from './store';

type Fetcher<T> = () => Promise<T>;
type Listener<T = unknown> = (data: T) => void;

/** Per-call choices a caller of `readThrough` can make. */
export interface ReadOptions {
  /**
   * Read network-first this once, past a cache-first ttl, and keep the cached
   * copy as the fallback. A refresh that deleted the copy first would lose it
   * when the fetch then fails offline.
   */
  refresh?: boolean | undefined;
}

/** Everything about one request that must not change while it is in flight. */
interface RequestScope {
  key: string;
  account: string;
  generation: number;
  /** The entity this read answers for, or null when the policy names none. */
  entityKey: string | null;
  /** The policy the read is running under, carried so the write-through can merge. */
  policy: ReadPolicy;
}

/**
 * Why a cached key's meaning changed: the queued write behind it reached the
 * server, or was thrown away. An editor waiting on its save needs to know
 * which, so it can say "saved" for the one and reload for the other.
 */
export type InvalidationReason = 'acknowledged' | 'discarded';
type InvalidationListener = (reason: InvalidationReason) => void;

const listeners = new Map<string, Set<Listener>>();
const revalidating = new Set<string>();
const invalidationListeners = new Map<string, Set<InvalidationListener>>();

/** Be told, with the reason, when a cached key changed meaning, so a screen can refetch. Returns a disposer. */
export function subscribeToInvalidation(key: string, listener: InvalidationListener): () => void {
  const set = invalidationListeners.get(key) ?? new Set<InvalidationListener>();
  set.add(listener);
  invalidationListeners.set(key, set);
  return () => {
    set.delete(listener);
    if (set.size === 0) invalidationListeners.delete(key);
  };
}

/** Fire the invalidation listeners for a key, in this tab only. */
export function notifyInvalidated(key: string, reason: InvalidationReason): void {
  invalidationListeners.get(key)?.forEach(listener => {
    try {
      listener(reason);
    } catch {
      // One broken subscriber must not stop the others.
    }
  });
}

type InvalidatedMessage = {
  type?: string;
  from?: string;
  keys?: string[];
  reason?: InvalidationReason;
};

const isReason = (value: unknown): value is InvalidationReason =>
  value === 'acknowledged' || value === 'discarded';

/**
 * Fire the invalidation listeners here and in every other tab.
 *
 * A queued write that reached the server changed what its cached copies mean
 * (a revision, a server id), and the screen showing one may be in a tab other
 * than the one that flushed. The reason travels with the keys: a listener
 * that only hears "changed" cannot tell a landed save from a discarded one.
 */
export function broadcastInvalidated(keys: string[], reason: InvalidationReason): void {
  keys.forEach(key => notifyInvalidated(key, reason));
  if (keys.length === 0 || typeof BroadcastChannel === 'undefined') return;
  const bc = new BroadcastChannel(OFFLINE_CHANNEL);
  bc.postMessage({ type: 'invalidated', from: TAB_ID, keys, reason });
  bc.close();
}

/** Deliver other tabs' invalidations to this tab's listeners. Installed once at boot; returns a disposer. */
export function listenForInvalidations(): () => void {
  if (typeof BroadcastChannel === 'undefined') return () => undefined;
  const bc = new BroadcastChannel(OFFLINE_CHANNEL);
  bc.onmessage = (event: MessageEvent<InvalidatedMessage>) => {
    const message = event.data;
    if (message?.type !== 'invalidated' || message.from === TAB_ID || !message.keys) return;
    // A message with no usable reason (an older build in another tab) is
    // dropped: a listener acting on a guessed reason would be wrong half the time.
    const reason = message.reason;
    if (!isReason(reason)) return;
    message.keys.forEach(key => notifyInvalidated(key, reason));
  };
  return () => bc.close();
}

/** Cache key for a GET: method, path, and the query with its keys sorted. */
export function resourceKey(url: string): string {
  const target = withoutFragment(url);
  const q = target.indexOf('?');
  if (q === -1) return `GET ${target}`;
  const params = new URLSearchParams(target.slice(q + 1));
  params.sort();
  return `GET ${target.slice(0, q)}?${params.toString()}`;
}

/**
 * Be handed the fresh data for a key when a background revalidation lands it.
 *
 * The data comes with the notification rather than the subscriber reading it
 * back: a re-read of a key whose ttl has already elapsed revalidates, which
 * notifies, which reads again — a loop the cache cannot break from the inside.
 */
export function subscribeToResource<T>(key: string, listener: (data: T) => void): () => void {
  const entry = listener as Listener;
  const set = listeners.get(key) ?? new Set<Listener>();
  set.add(entry);
  listeners.set(key, set);
  return () => {
    set.delete(entry);
    if (set.size === 0) listeners.delete(key);
  };
}

/** Hand every subscriber of a key the fresh data that has landed for it. */
function notify(key: string, data: unknown): void {
  listeners.get(key)?.forEach(listener => {
    // One subscriber's failure is its own; the rest still need telling.
    try {
      listener(data);
    } catch (err) {
      logger.warn('offline: a resource subscriber threw', err);
    }
  });
}

/**
 * Drop one key's cached copy, so the next read of it has to reach the server.
 *
 * A refresh that follows a create or a delete must not be answered from the
 * copy taken before it, which a cache-first read inside its ttl would do.
 * Signed out there is no store to touch, and a store that refuses the delete
 * is not the caller's problem: this never throws.
 */
export async function invalidateResource(key: string): Promise<void> {
  const account = currentAccountId();
  if (!account) return;
  await deleteResource(key, account);
}

/**
 * Serve the cached copy without revalidating when the entity has rows queued.
 *
 * While a write for an entity is waiting to reach the server, the cached copy
 * carries that intent and the server's copy does not: answering from the
 * network here would show the user their own change undone. Null when the
 * policy names no entity, when nothing is queued for it, or on a cache miss.
 */
async function authoritativeHit<T>(scope: RequestScope): Promise<ResourceRow<T> | null> {
  if (!scope.entityKey) return null;
  const rows = await listOutbox(scope.account);
  if (!rows.some(row => row.entityKey === scope.entityKey)) return null;
  return getResource<T>(scope.key, scope.account);
}

/**
 * A list policy's chance to fold every queued row into a fresh response.
 *
 * All rows are offered, not just the ones for this key's entity: a list shows
 * rows the reader's own entity key knows nothing about (a create still queued
 * has its own entity), so the policy filters for what belongs in it.
 */
async function mergeQueued<T>(
  tx: OfflineTx<'readwrite'>,
  scope: RequestScope,
  data: T
): Promise<T> {
  const { mergePending } = scope.policy;
  if (!mergePending) return data;
  const allQueued = (await tx.objectStore('outbox').getAll()).sort((a, b) => a.seq - b.seq);
  return allQueued.length > 0 ? (mergePending(data, allQueued) as T) : data;
}

/** What a write-through settled on: the data to serve, and whether any of it was stored. */
interface WriteThrough<T> {
  data: T;
  stored: boolean;
}

/**
 * What one write-through decided to store and hand back, inside its transaction.
 *
 * The outbox is consulted in the same transaction that would write the
 * response through, so an enqueue that lands while the response is in flight
 * cannot slip between the check and the write: with rows queued and a cached
 * copy, the response is dropped in favour of what the outbox already wrote;
 * with rows queued and no copy (a cold cache), the response is stored and the
 * queued echoes are re-applied over it in seq order.
 *
 * Every await below resolves a request issued on `tx` — which is why
 * `mergeQueued` is called only when it has a `getAll` to make. An await that
 * resolves without a pending request lets the browser deactivate the
 * transaction, and every store call after it then throws into the caller's
 * catch, which quietly serves the fetched data and never fills the cache.
 */
async function storeResponse<T>(
  tx: OfflineTx<'readwrite'>,
  scope: RequestScope,
  data: T
): Promise<WriteThrough<T>> {
  const queued = scope.entityKey ? await queuedRowsIn(tx, scope.entityKey) : [];
  const existing = (await tx.objectStore('resources').get(scope.key))?.data as T | undefined;
  if (queued.length > 0 && existing !== undefined) return { data: existing, stored: false };
  const merged = scope.policy.mergePending ? await mergeQueued(tx, scope, data) : data;
  await tx.objectStore('resources').put({ key: scope.key, data: merged, fetchedAt: Date.now() });
  if (queued.length === 0) {
    // A fresh read with nothing queued is the newest truth; a revision
    // threaded from an earlier reply is stale beside it.
    if (scope.entityKey) await tx.objectStore('meta').delete(revisionKey(scope.entityKey));
    return { data: merged, stored: true };
  }
  await applyEchoes(tx, queued);
  const rebuilt = (await tx.objectStore('resources').get(scope.key))?.data as T | undefined;
  return { data: rebuilt ?? merged, stored: true };
}

/**
 * Fetch and write through, for the account and generation the request started
 * in, and never fail the read for the cache's sake.
 *
 * A store that will not open, will not write, or has moved on to another
 * account leaves the caller with the data the server just gave it.
 */
async function fetchAndStore<T>(scope: RequestScope, fetcher: Fetcher<T>): Promise<T> {
  const data = await fetcher();
  let outcome: WriteThrough<T> | null = null;
  try {
    outcome = await withStores<WriteThrough<T>, 'readwrite'>(
      { account: scope.account, generation: scope.generation },
      'readwrite',
      tx => storeResponse(tx, scope, data)
    );
  } catch (err) {
    logger.debug('offline: write-through failed, serving the fetched data', err);
    return data;
  }
  // Signed out or wiped while the response was in flight: nothing was stored.
  if (!outcome) return data;
  // A response the outbox overruled was never stored, so it must not be
  // recorded as a fresh fetch of this key: what is on screen is the echo.
  if (!outcome.stored) return outcome.data;
  recordFreshness(scope.key, { fetchedAt: Date.now(), servedFromCache: false, stale: false });
  void noteFirstCacheWrite(scope.account);
  return outcome.data;
}

/**
 * Background refresh of a stale hit; one at a time per key, and never fatal.
 *
 * "In flight" is tracked per account as well as per key: one account's
 * revalidation must not stand in the way of the same endpoint's revalidation
 * for whoever signs in next.
 */
function revalidate<T>(scope: RequestScope, fetcher: Fetcher<T>): void {
  const inFlight = `${scope.account} ${scope.key}`;
  if (revalidating.has(inFlight)) return;
  revalidating.add(inFlight);
  void fetchAndStore(scope, fetcher)
    // The write may have been dropped — a wipe, a full store — but the data is
    // still the freshest there is, so subscribers get it either way.
    .then(data => notify(scope.key, data))
    .catch((err: unknown) => {
      // Being offline is the expected case here, not something to report.
      if (!isNetworkError(err)) logger.warn('offline: revalidation failed for', scope.key, err);
    })
    .finally(() => revalidating.delete(inFlight));
}

/** Serve a cached hit (revalidating in the background if stale); fetch on a miss. */
async function cacheFirst<T>(scope: RequestScope, ttlMs: number, fetcher: Fetcher<T>): Promise<T> {
  const owned = await authoritativeHit<T>(scope);
  if (owned) {
    recordFreshness(scope.key, { fetchedAt: owned.fetchedAt, servedFromCache: true, stale: false });
    return owned.data;
  }
  const hit = await getResource<T>(scope.key, scope.account);
  if (hit) {
    const stale = Date.now() - hit.fetchedAt >= ttlMs;
    // Recorded before the revalidation is launched: a revalidation that lands
    // synchronously would otherwise have its fresh record overwritten by this
    // older one.
    recordFreshness(scope.key, { fetchedAt: hit.fetchedAt, servedFromCache: true, stale });
    if (stale) revalidate(scope, fetcher);
    return hit.data;
  }
  try {
    return await fetchAndStore(scope, fetcher);
  } catch (err) {
    if (isNetworkError(err)) throw new OfflineError(scope.key);
    throw err;
  }
}

/** Prefer the network; fall back to a cached hit only on a network failure. */
async function networkFirst<T>(scope: RequestScope, fetcher: Fetcher<T>): Promise<T> {
  const owned = await authoritativeHit<T>(scope);
  if (owned) {
    recordFreshness(scope.key, { fetchedAt: owned.fetchedAt, servedFromCache: true, stale: false });
    return owned.data;
  }
  try {
    return await fetchAndStore(scope, fetcher);
  } catch (err) {
    if (!isNetworkError(err)) throw err;
    const hit = await getResource<T>(scope.key, scope.account);
    if (hit) {
      recordFreshness(scope.key, { fetchedAt: hit.fetchedAt, servedFromCache: true, stale: true });
      return hit.data;
    }
    throw new OfflineError(scope.key);
  }
}

/**
 * Run a GET through its read policy.
 *
 * Signed out, or `network-only`: the fetcher runs as-is and nothing is stored.
 */
export async function readThrough<T>(
  url: string,
  fetcher: Fetcher<T>,
  options?: ReadOptions
): Promise<T> {
  const policy = readPolicyFor(url);
  const account = currentAccountId();
  if (policy.strategy === 'network-only' || !account) return fetcher();
  const scope: RequestScope = {
    key: resourceKey(url),
    account,
    generation: currentStoreGeneration(account),
    entityKey: policy.entity?.(url) ?? null,
    policy,
  };
  return policy.strategy === 'cache-first' && !options?.refresh
    ? cacheFirst(scope, policy.ttlMs, fetcher)
    : networkFirst(scope, fetcher);
}
